import React, { Fragment, useEffect, useState } from 'react'
import { UPDATE_QUESTION_ACTIONS } from '../../model/editableQuestion/actions'
import {
	ClientFields,
	ClozeDropdown,
	StudentAnswer as StudentAnswerType,
	getTemplateKeys as getTemplateKeysPositions,
	parseTemplateStringIntoSegments,
	TemplateSegmentType,
} from '@mission.io/question-toolkit'
import { TeacherQuestionProps } from '../Question'
import UploadImage from '../../components/upload-image/UploadImage'
import {
	getChoiceHandlers,
	getImageMeta,
	isClozeDropdown,
	isDropdownAnswer,
	isStudentDropdownAnswerCorrect,
} from '../../utils'
import { TemplateField } from '../../components/template-field/TemplateField'
import { DropdownTabs, TabPanel } from '../../components/dropdown-tabs'
import EditFooter from '../../components/edit-footer'
import { QuestionLabel } from '../../components/teacher-header/TeacherHeader'
import {
	AddButton,
	InputGroup,
	ReadOnly,
	QuestionResponseStyled,
	UploadImageWrapper,
	QuestionHeader,
} from '../../assets/styles'
import { buildTemplatePhrase } from './Student'
import Collapsible from '../../components/collapsible'
import ChoicesResponse from '../../components/choices-response/ChoicesResponse'
import AnalyticsSingleStudentContainer from '../../components/analytics-single-student/AnalyticsSingleStudent'
import ChoicesReadOnly from '../../components/choices-readonly'
import { DropdownClozeDropdown } from '../../model/general'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { QuestionsForm } from '@mission.io/question-views'
import { isMarkdownString } from '../../components/Markdown/hooks'
import {
	ClozeDropdownProvider,
	getTemplateMarkdownString,
	TEXT_TO_SPEECH_CLOSE_READING_MARKDOWN_OVERRIDES,
} from './helpers'
import OptionalMarkdown from '../../components/Markdown/OptionalMarkdown'

/**
 * A component for a teacher View where can create the ClozeDropdown question. It also has a readOnly view
 * where it can't be edited
 * @returns {JSX.Element}
 */
export default function TeacherClozeDropdown({
	isCollapsibleQuestion,
	question,
	onChange,
	questionIndex = 0,
	readOnly,
	isQuestionAnalytics,
	studentName,
	studentResponse,
	onRequestClose,
	buttonAction,
	autoFocus = false,
}: TeacherQuestionProps): React.ReactNode {
	if (!question || !isClozeDropdown(question)) return <Fragment />

	const [cursorPosition, setCursorPosition] = useState(0)
	const [templateUpdated, setTemplateUpdated] = useState(1)
	const [dropdownIndexSelected, setDropdownIndexSelected] = useState<number>(-1)

	const questionMedia = question.media?.find(
		mediaItem => mediaItem.type === 'IMAGE'
	)
	const doesQuestionHaveImg = !!questionMedia

	const {
		handleDeleteChoice,
		handleAddChoice,
		handleAnswerChange,
		handleChange,
	} = getChoiceHandlers(onChange, readOnly)

	const handleAddDropDown = (pos?: number) => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.ADD_DROPDOWN,
				payload: pos && !isNaN(pos) ? pos : cursorPosition,
			})

			setTemplateUpdated(templateUpdated * -1)
		}
	}
	const handleDeleteDropDown = (dropdownId: string) => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.DELETE_DROPDOWN,
				payload: dropdownId,
			})
			setTemplateUpdated(templateUpdated * -1)
			handleChangeSelectedTab(0)
		}
	}

	useEffect(() => {
		if (dropdownIndexSelected === -1 && question.dropdowns.length > 0) {
			setDropdownIndexSelected(0)
		}
	}, [dropdownIndexSelected, question.dropdowns])

	const handleChangeSelectedTab = (dropdownIndex: number) => {
		setDropdownIndexSelected(dropdownIndex)
		document.getElementById(`${dropdownIndex + 1}-${questionIndex}`)?.focus()
	}
	const handleChangeQuestionImage = (file: File | Blob) => {
		if (!readOnly && onChange) {
			const { localUrl } = getImageMeta(file)

			onChange({
				type: UPDATE_QUESTION_ACTIONS.CHANGE_QUESTION_IMAGE,
				payload: {
					type: 'IMAGE',
					file: file,
					localUrl: localUrl,
				},
			})
		}
	}
	const handleDeleteQuestionImage = () => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.DELETE_QUESTION_IMAGE,
			})
		}
	}

	const handleChangeTemplate = (update: QuestionsForm.ChangeTemplateDiff) => {
		if (onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.CHANGE_TEMPLATE,
				payload: update,
			})

			if (validateDropdowns(update.newTemplate, question)) {
				setTemplateUpdated(templateUpdated * -1)
			}
		}
	}

	if (isQuestionAnalytics) {
		if (!onRequestClose) return null
		return (
			<AnalyticsSingleStudentContainer
				question={question}
				title="Modeling Question"
				studentResponse={studentResponse}
				onRequestClose={onRequestClose}
				studentName={studentName}>
				<QuestionResponse
					question={question}
					studentAnswer={studentResponse?.answer}
				/>
			</AnalyticsSingleStudentContainer>
		)
	}

	const [expand, setExpand] = useState(false)
	if (isCollapsibleQuestion) {
		return (
			<Collapsible
				expand={expand}
				header={
					<div className="font-medium">
						<span className="notranslate">Q:&nbsp;</span>
						{buildTemplatePhrase({
							template: question.template,
							dropdowns: question.dropdowns,
							renderDropdownButton({ dropdownId }: { dropdownId: string }) {
								return (
									<span className="bg-brand-dusk text-brand-deep-blue rounded-lg inline-flex justify-center w-6 font-semibold leading-[1.35rem]">
										{dropdownId}
									</span>
								)
							},
						})}
					</div>
				}
				buttonAction={buttonAction}
				handleClick={() => setExpand(state => !state)}>
				<DropdownTabs
					indexSelected={dropdownIndexSelected}
					question={question}
					handleChange={handleChangeSelectedTab}
				/>
				<TabPanel
					indexSelected={dropdownIndexSelected}
					dropdowns={question.dropdowns}
					readOnly={true}
				/>
			</Collapsible>
		)
	}

	return !readOnly ? (
		<div className="bg-brand-cloud-blue text-brand-space-blue rounded-lg">
			<div className="p-4">
				<div className="text-start">
					<div className="font-semibold mb-2">Cloze Dropdown</div>
					<div className="text-sm mb-2">
						Click “Add Dropdown” to insert a dropdown into your question.
					</div>
				</div>
				<InputGroup>
					<QuestionLabel>
						<span className="notranslate">{`Q${questionIndex + 1}`}</span>
					</QuestionLabel>
					<TemplateField
						setCursorPosition={setCursorPosition}
						onSelectTab={handleChangeSelectedTab}
						onDeleteImage={handleDeleteQuestionImage}
						value={question.template}
						media={question.media}
						onChange={handleChangeTemplate}
						templateUpdated={templateUpdated}
						questionIndex={questionIndex}
						autoFocus={autoFocus}
					/>
					<UploadImageWrapper>
						<UploadImage
							disabled={doesQuestionHaveImg}
							onChange={handleChangeQuestionImage}
							readOnly={readOnly}
						/>
					</UploadImageWrapper>
				</InputGroup>
				<div>
					{isMarkdownString(question.template) ? (
						<TemplateReadOnly template={question.template} />
					) : null}
				</div>
				<AddButton onClick={() => handleAddDropDown()}>Add Dropdown</AddButton>
				{question.dropdowns.length > 0 && (
					<div className="mt-4">
						<DropdownTabs
							indexSelected={dropdownIndexSelected}
							question={question}
							handleClose={handleDeleteDropDown}
							handleChange={handleChangeSelectedTab}
						/>
						<TabPanel
							indexSelected={dropdownIndexSelected}
							dropdowns={question.dropdowns}
							handleAddChoice={handleAddChoice}
							handleAnswerChange={handleAnswerChange}
							handleDeleteChoice={handleDeleteChoice}
							handleChange={handleChange}
							autoFocus={autoFocus}
						/>
					</div>
				)}
			</div>
			<EditFooter onChange={onChange} />
		</div>
	) : (
		<ReadOnly className="Question">
			<div>
				<QuestionHeader>
					<span>
						<strong className="notranslate">
							{`Q${questionIndex + 1}.`}&nbsp;&nbsp;
						</strong>
					</span>
					Curriculum Questions - Cloze Dropdown
				</QuestionHeader>
			</div>
			<div className="template-wrapper">
				<TemplateReadOnly template={question.template} />
			</div>
			<div className="flex flex-wrap gap-2">
				{question.dropdowns.map(
					(item: DropdownClozeDropdown, index: number) => (
						<ChoicesReadOnly
							key={`dropdown-${index}`}
							choices={item.choices}
							index={index}
						/>
					)
				)}
			</div>
		</ReadOnly>
	)
}

/**
 * This function makes the answer box for Analytics Student answer.
 * We just output the student answer.
 */
function QuestionResponse({
	question,
	studentAnswer,
}: {
	question: ClozeDropdown<ClientFields>
	studentAnswer?: StudentAnswerType
}): JSX.Element {
	if (studentAnswer && !isDropdownAnswer(studentAnswer)) return <Fragment />

	return (
		<QuestionResponseStyled css="background: transparent;">
			<ul>
				{question.dropdowns.map(
					(dropdown: DropdownClozeDropdown, index: number) => {
						const isCorrect = isStudentDropdownAnswerCorrect(
							dropdown,
							dropdown.templateResponseKey,
							studentAnswer
						)
						return (
							<li
								role="option"
								className="list-none p-2 bg-brand-ultra-soft-violet my-2 rounded-lg"
								key={index}>
								<div className="font-bold m-2 pl-2">
									<IndexQuestion>{`${index + 1}.`}</IndexQuestion>
									{`${isCorrect ? 'Correct' : 'Incorrect'}`}
								</div>
								<ChoicesResponse
									choices={dropdown.choices}
									studentChoiceSelected={String(
										studentAnswer?.value[dropdown.templateResponseKey]
									)}
								/>
							</li>
						)
					}
				)}
			</ul>
		</QuestionResponseStyled>
	)
}

const IndexQuestion = styled.div`
	display: inline-block;
	margin: auto 8px auto 0;
	width: 16px;
`

/**
 * A component that validate if template is correctly written:
 * @param {string} template
 * @return {boolean}
 */
function validateDropdowns(
	template: string,
	question: ClozeDropdown<ClientFields>
): boolean {
	const keysPosition = getTemplateKeysPositions(template)

	return keysPosition && keysPosition.length !== question.dropdowns.length
		? true
		: false
}

/**
 * Creates the read only html for the given Cloze Dropdown template text
 * @param {string} template the template string for a cloze dropdown question
 * @param {(index: number) => void} onClick
 * @return {string}
 */
export function TemplateReadOnly({
	template,
}: {
	template: string
}): JSX.Element {
	function dropdownButton({ dropdownId }: { dropdownId: string }) {
		return (
			<button
				disabled
				className={
					'rounded-lg px-2 bg-brand-horizon-blue text-brand-cloud-blue'
				}>
				{dropdownId}
			</button>
		)
	}

	const markdownTemplateString = getTemplateMarkdownString(template)
	if (markdownTemplateString != null) {
		const dropdownComponents: Record<string, JSX.Element> = {}
		parseTemplateStringIntoSegments(template).forEach(segment => {
			if (segment.type === TemplateSegmentType.Input) {
				dropdownComponents[segment.inputId] = dropdownButton({
					dropdownId: segment.inputId,
				})
			}
		})
		return (
			<div aria-label="Template Content" className="text-left">
				<ClozeDropdownProvider
					state={{
						dropDowns: dropdownComponents,
					}}>
					<OptionalMarkdown
						interpretAsMarkdown={true}
						overrides={TEXT_TO_SPEECH_CLOSE_READING_MARKDOWN_OVERRIDES}>
						{markdownTemplateString}
					</OptionalMarkdown>
				</ClozeDropdownProvider>
			</div>
		)
	}

	return (
		<div aria-label="Template Content" className="text-left">
			{parseTemplateStringIntoSegments(template).map((segment, index) => {
				const segmentKey = `segment-${index}`
				if (segment.type === TemplateSegmentType.Text) {
					const text = segment.text
					return <Fragment key={segmentKey}>{text}</Fragment>
				}
				if (segment.type === TemplateSegmentType.Input) {
					return (
						<Fragment key={segmentKey}>
							{dropdownButton({ dropdownId: segment.inputId })}
						</Fragment>
					)
				}
				return <Fragment key={segmentKey} />
			})}
		</div>
	)
}
